import { useMutation, useQueryClient, InfiniteData } from '@tanstack/react-query';
import { useFocusEffect } from 'expo-router';
import { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';

import type { ChatConversation } from '@/services/chat/chatTransport';
import type { GetInquiriesV2Response, InquiryV2 } from '@/services/getInquiriesV2';
import { markInquiryReadV2 } from '@/services/markInquiryReadV2';
import { clearInboxUnread } from '@/util/clearInboxUnread';
import { invalidateUnreadInquiriesDebounced } from '@/util/inboxInvalidation';

type UseMarkConversationAsReadOptions = {
  conversation: ChatConversation | undefined;
  conversationStatus: InquiryV2['inquiryStatus'] | undefined;
  /**
   * When true, marks as read when the conversation is viewed.
   * Web: on conversation change. Mobile: on screen focus.
   */
  autoMarkOnView?: boolean;
};

const isInquiriesV2Query = (queryKey: unknown) =>
  Array.isArray(queryKey) && queryKey[0] === 'inquiriesV2' && queryKey.length >= 2;

const updateUnreadStateInCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  shouldMarkRead: (conversationId: string, hasUnread: boolean | undefined) => boolean
) => {
  queryClient.setQueriesData<InfiniteData<GetInquiriesV2Response>>(
    {
      predicate: (query) => isInquiriesV2Query(query.queryKey),
    },
    (prev) => clearInboxUnread(prev, shouldMarkRead)
  );
};

/**
 * Marks a conversation as read in both Twilio SDK and backend DB.
 * Updates Twilio SDK, persists to backend DB, and invalidates unread counts.
 *
 * Treats backend as source of truth for unread state. When autoMarkOnView is true
 * and the inquiries cache reports hasUnread for this conversation, syncs the backend
 * via POST /read even if the conversation's lastReadIndex already indicates read (e.g.
 * after another device or a previous session).
 *
 * `markAsRead` accepts:
 * - `forceBackendSync` — always POST /read regardless of cache/Twilio state
 *   (used by markReadOnOpen to handle stale cross-device unread).
 * - `siblingConversationIds` — other conversation IDs in the same inquiry group.
 *   When provided, optimistically clears hasUnread on all siblings in the cache
 *   and fires backend mark-read for each, so the entire group reads as read.
 */
export function useMarkConversationAsRead({
  conversation,
  conversationStatus,
  autoMarkOnView = false,
}: UseMarkConversationAsReadOptions) {
  const queryClient = useQueryClient();

  const conversationRef = useRef<ChatConversation | undefined>(undefined);

  conversationRef.current = conversation;

  const inFlightRef = useRef(false);

  const lastAutoMarkedSidRef = useRef<string | undefined>(undefined);

  const lastPostedAtRef = useRef<number>(0);

  const markReadMutation = useMutation({
    mutationFn: (conversationId: string) => markInquiryReadV2(conversationId),
    onMutate: async (conversationId) => {
      updateUnreadStateInCache(
        queryClient,
        (cachedConversationId) => cachedConversationId === conversationId
      );
    },
    onSettled: () => {
      invalidateUnreadInquiriesDebounced(queryClient, 0);

      queryClient
        .invalidateQueries({ queryKey: ['inquiriesV2'], refetchType: 'none' })
        .catch(() => undefined);
    },
  });

  const markSiblingsReadInCache = useCallback(
    (siblingIds: string[]) => {
      const idSet = new Set(siblingIds);

      updateUnreadStateInCache(
        queryClient,
        (conversationId, hasUnread) => idSet.has(conversationId) && Boolean(hasUnread)
      );
    },
    [queryClient]
  );

  const markAsRead = useCallback(
    async (options?: { forceBackendSync?: boolean; siblingConversationIds?: string[] }) => {
      const forceBackendSync = options?.forceBackendSync ?? false;

      const siblingConversationIds = options?.siblingConversationIds;

      const c = conversationRef.current;

      if (!c) {
        return;
      }

      if (inFlightRef.current) {
        return;
      }

      const now = Date.now();

      if (now - lastPostedAtRef.current < 1500) {
        return;
      }

      const lastRead = c.lastReadIndex;

      const lastMessage = c.lastMessage?.index;

      const inquiriesCache = queryClient.getQueriesData<InfiniteData<GetInquiriesV2Response>>({
        predicate: (query) => isInquiriesV2Query(query.queryKey),
      });

      const backendHasUnread = inquiriesCache.some(([, data]) =>
        data?.pages?.some((page) =>
          page.inquiries?.some((inquiry) => inquiry.conversationId === c.id && inquiry.hasUnread)
        )
      );

      const shouldSyncBackend = backendHasUnread || forceBackendSync;

      if (!shouldSyncBackend) {
        if (lastMessage === null || lastMessage === undefined) {
          return;
        }

        if (lastRead !== null && lastRead !== undefined && lastRead >= lastMessage) {
          return;
        }
      }

      if (siblingConversationIds?.length) {
        markSiblingsReadInCache(siblingConversationIds);
      }

      try {
        inFlightRef.current = true;

        lastPostedAtRef.current = now;

        await c.markAllRead().catch(() => undefined);

        const markReadPromises: Promise<unknown>[] = [markReadMutation.mutateAsync(c.id)];

        if (siblingConversationIds?.length) {
          for (const siblingId of siblingConversationIds) {
            markReadPromises.push(markInquiryReadV2(siblingId).catch(() => undefined));
          }
        }

        await Promise.all(markReadPromises);
      } finally {
        inFlightRef.current = false;

        if (siblingConversationIds?.length) {
          invalidateUnreadInquiriesDebounced(queryClient, 0);
        }
      }
    },
    [conversationStatus, markReadMutation, markSiblingsReadInCache, queryClient]
  );

  useEffect(() => {
    if (!autoMarkOnView || !conversation) {
      return;
    }

    if (Platform.OS !== 'web') {
      return;
    }

    if (lastAutoMarkedSidRef.current === conversation.id) {
      return;
    }

    lastAutoMarkedSidRef.current = conversation.id;

    markAsRead().catch(() => undefined);
  }, [autoMarkOnView, conversation?.id, markAsRead, conversation]);

  useFocusEffect(
    useCallback(() => {
      if (!autoMarkOnView || !conversation) {
        return;
      }

      if (Platform.OS === 'web') {
        return;
      }

      if (lastAutoMarkedSidRef.current === conversation.id) {
        return;
      }

      lastAutoMarkedSidRef.current = conversation.id;

      markAsRead().catch(() => undefined);
    }, [autoMarkOnView, conversation, markAsRead])
  );

  return {
    markAsRead,
    isPending: markReadMutation.isPending,
    isError: markReadMutation.isError,
  };
}
